/**
 * Battle pass (ตั๋วยุทธภพ): season XP from fighting and quests climbs tiers, each with a free reward
 * and a premium one. Premium is bought with jade and pays only looks, titles and jade back — never
 * strength — so the free track is the one that helps a character grow.
 */
export interface PassReward {
  readonly jade?: number;
  readonly gold?: number;
  readonly cultivation?: number;
  readonly skin?: string;
  readonly mount?: string;
  readonly title?: string;
}

export interface PassTier {
  readonly level: number;
  readonly free: PassReward;
  readonly premium: PassReward;
}

export type PassTrack = 'free' | 'premium';

export const SEASON = {
  id: 's1',
  name: 'ฤดูกาลที่ 1 · ศิลาเก้ากระบี่',
  /** Shown to players; the server enforces the dates once it exists (phase 2). */
  starts: '2026-10-01',
  ends: '2026-12-31',
  maxLevel: 30,
  xpPerLevel: 100,
  /** Jade for the premium track. */
  premiumPrice: 980,
  xp: { questTurnIn: 120 },
} as const;

/** Season XP for a kill: a third of the monster's cultivation reward, at least 2. */
export const passXpForKill = (cultivation: number): number => Math.max(2, Math.round(cultivation / 3));

export const passLevel = (xp: number): number =>
  Math.min(SEASON.maxLevel, Math.floor(xp / SEASON.xpPerLevel));

const PREMIUM_SPECIAL: Readonly<Record<number, PassReward>> = {
  1: { jade: 100 },
  10: { skin: 'azure-fox' },
  20: { skin: 'violet-fan-swords' },
  25: { skin: 'purple-dew' },
  30: { title: 's1-sovereign', jade: 300 },
};

export const PASS_TIERS: readonly PassTier[] = Array.from({ length: SEASON.maxLevel }, (_, i) => {
  const level = i + 1;
  const free: PassReward =
    level === SEASON.maxLevel
      ? { title: 's1-wanderer', jade: 50 }
      : level % 5 === 0
        ? { jade: 30 }
        : level % 2
          ? { gold: 300 + level * 20 }
          : { cultivation: 100 + level * 10 };
  const premium = PREMIUM_SPECIAL[level] ?? { jade: level % 5 === 0 ? 80 : 20 };
  return { level, free, premium };
});
